import React from 'react';
import { Text, StyleSheet, View, Platform, ScrollView } from 'react-native';
import _ from 'lodash';
import EmptyState from 'src/presentation/components/molecules/EmptyState';
import FavoTimeTableItem from 'src/presentation/components/molecules/FavoTimeTableItem';
import Title from 'src/presentation/components/atoms/Title';
import { useEventStore } from 'src/domain/stores/useEventStore';
import { useMyBarTimeTableStore } from 'src/domain/stores/useMyBarTimeTableStore';
import type { BarTimetableItem } from 'src/domain/stores/useMyBarTimeTableStore';
import Dimens from 'src/core/utils/Dimens';
import Translations from 'src/core/utils/Translations';
import Fonts from 'src/core/utils/Fonts';
import Colors from 'src/core/utils/Colors';
import isFavoritesPage from 'src/core/utils/isFavoritesPage';
import type { ApiPageTimetableHolder } from 'src/core/models/api/ApiPage';

import type { TimeTablePageScreenParams } from 'src/presentation/navigation/types/NavigationTypes';

interface MyTimeTableProps {
	onNavigateToTimetablePage: (params: TimeTablePageScreenParams) => void;
}

type ProgramListEntry = BarTimetableItem | string;

/**
 * Only two known timestamps can overlap; null would compare as 0 (#400).
 */
export function overlaps(start: number | null | undefined, end: number | null | undefined): boolean {
	return typeof start === 'number' && typeof end === 'number' && start < end;
}

function MyTimeTable({ onNavigateToTimetablePage }: MyTimeTableProps) {
	const eventStore = useEventStore();
	const myBarTimeTableStore = useMyBarTimeTableStore();

	function checkIfProgramDatesAreEqual(array: Array<string | undefined>) {
		const first = array[0];
		return array.every((value) => value === first || value === undefined);
	}

	function renderMultipleDaysTimeTableList(
		_programHolder: ApiPageTimetableHolder,
		favoProgramItems: BarTimetableItem[],
	) {
		const arraylist: ProgramListEntry[] = [];
		const sorted = _.sortBy(favoProgramItems, (i) => i.item.startdate);

		sorted.forEach((programItem) => {
			if (!arraylist.includes(programItem.item.navTitle)) {
				arraylist.push(programItem.item.navTitle);
			}
			arraylist.push(programItem);
		});

		const arrayLength = arraylist.length - 1;

		return arraylist.map((programItem, idx) => {
			if (typeof programItem === 'string') {
				const noDataNext = typeof arraylist[idx + 1] === 'string' || arraylist[idx + 1] === undefined;

				return (
					<View key={idx}>
						<Text style={styles.spacer}>{programItem}</Text>
						{noDataNext && (
							<View style={arrayLength === idx ? { borderBottomWidth: 0 } : undefined}>
								<Text style={styles.nofavorites}>{Translations.textNoFavoritesForDay}</Text>
							</View>
						)}
					</View>
				);
			} else {
				let flag = false;

				const prev = arraylist[idx - 1];
				const next = arraylist[idx + 1];

				if (
					(typeof prev !== 'string' && prev?.item && overlaps(programItem.item.startdate, prev.item.enddate)) ||
					(typeof next !== 'string' && next?.item && overlaps(next.item.startdate, programItem.item.enddate))
				) {
					flag = true;
				}

				const key = programItem.key;

				return (
					<FavoTimeTableItem
						data={programItem}
						flag={flag}
						key={key}
						noBorderBottom={arrayLength === idx}
						onNavigateToTimetablePage={onNavigateToTimetablePage}
					/>
				);
			}
		});
	}

	function renderMultipleStagesTimeTableList(
		_programHolder: ApiPageTimetableHolder,
		favoProgramItems: BarTimetableItem[],
	) {
		const arraylist: BarTimetableItem[] = _.sortBy(favoProgramItems, (i) => i.item.startdate);
		const arrayLength = arraylist.length - 1;

		return arraylist.map((programItem, idx) => {
			let flag = false;

			const prev = arraylist[idx - 1];
			const next = arraylist[idx + 1];

			if (
				(prev && overlaps(programItem.item.startdate, prev.item.enddate)) ||
				(next && overlaps(next.item.startdate, programItem.item.enddate))
			) {
				flag = true;
			}

			const key = programItem.key;

			return (
				<FavoTimeTableItem
					data={programItem}
					flag={flag}
					key={key}
					noBorderBottom={arrayLength === idx}
					onNavigateToTimetablePage={onNavigateToTimetablePage}
				/>
			);
		});
	}

	function renderTimeTableList() {
		const programHolder = eventStore.activeEventView.find(
			(p): p is ApiPageTimetableHolder => p.type === 'TimetableHolder',
		);
		if (!programHolder) return null;

		const favoProgramItems = myBarTimeTableStore.myTimeTableData.filter((i) => i.eventUuid === programHolder.event_uuid);

		const myTimeTable = programHolder.children.find((c) => isFavoritesPage(c));

		const programDates = programHolder.children
			.filter((child) => !isFavoritesPage(child))
			.map((child) => child.data.page_date);

		const checkIfEqual = checkIfProgramDatesAreEqual(programDates);

		if (favoProgramItems.length > 0 && !checkIfEqual) {
			return renderMultipleDaysTimeTableList(programHolder, favoProgramItems);
		} else if (favoProgramItems.length > 0 && checkIfEqual) {
			return renderMultipleStagesTimeTableList(programHolder, favoProgramItems);
		} else {
			const message =
				myTimeTable?.data && typeof myTimeTable.data === 'object' && 'description' in myTimeTable.data
					? ((myTimeTable.data as { description?: string }).description ?? Translations.textNoActivitiesInTimetable)
					: Translations.textNoActivitiesInTimetable;
			return (
				<View style={styles.emptyState}>
					<EmptyState icon="heart-outline" iconColor={Colors.red} message={message} />
				</View>
			);
		}
	}

	const holder = eventStore.activeEventView.find((p): p is ApiPageTimetableHolder => p.type === 'TimetableHolder');
	const favoItems = holder ? myBarTimeTableStore.myTimeTableData.filter((i) => i.eventUuid === holder.event_uuid) : [];

	// Empty case: render outside the ScrollView so the heart + message centre in the area below the title.
	if (favoItems.length === 0) {
		const holderTimeTable = holder?.children.find((c) => isFavoritesPage(c));
		const emptyMessage =
			holderTimeTable?.data && typeof holderTimeTable.data === 'object' && 'description' in holderTimeTable.data
				? ((holderTimeTable.data as { description?: string }).description ?? Translations.textNoActivitiesInTimetable)
				: Translations.textNoActivitiesInTimetable;
		return (
			<View style={styles.emptyWrapper}>
				{/* Title as a top overlay so the heart + message centre in the full area (not just below
				    the title), which reads as properly centred instead of sitting slightly low. */}
				<View style={styles.emptyTitle}>
					<Title text={Translations.titleMyTimetable} />
				</View>
				<EmptyState icon="heart-outline" iconColor={Colors.red} message={emptyMessage} />
			</View>
		);
	}

	return (
		<ScrollView contentContainerStyle={styles.scrollContent} style={StyleSheet.flatten(styles.contentContainer)}>
			<View renderToHardwareTextureAndroid={true} style={styles.list}>
				<Title text={Translations.titleMyTimetable} />
				<View
					style={[
						styles.list,
						{
							borderBottomWidth: 0,
							borderTopWidth: 0,
						},
					]}
				>
					{renderTimeTableList()}
				</View>
			</View>
		</ScrollView>
	);
}

export default MyTimeTable;

const styles = StyleSheet.create({
	contentContainer: {
		flex: 1,
		marginBottom: Dimens.margin35,
	},
	scrollContent: {
		flexGrow: 1,
	},
	emptyState: {
		flex: 1,
		justifyContent: 'center',
		alignItems: 'center',
	},
	emptyWrapper: {
		flex: 1,
		backgroundColor: Colors.white,
	},
	emptyTitle: {
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
	},
	spacer: {
		fontSize: Dimens.fontSize17,
		fontFamily: Fonts.montserratBold,
		color: Colors.greyTextDark,
		// Padding, not margin: the header spans the full width (it carries the separator) while its text lines up
		// with the rows below, which sit at the same distance.
		paddingHorizontal: Dimens.padding20,
		paddingTop: Dimens.padding16,
		paddingBottom: Dimens.padding16,
	},
	nofavorites: {
		fontSize: Dimens.fontSize17,
		fontFamily: Fonts.montserratRegular,
		color: Colors.greyTextDark,
		paddingHorizontal: Dimens.padding20,
		paddingBottom: Dimens.padding16,
	},
	list: {
		backgroundColor: Colors.white,
		borderBottomWidth: Platform.OS === 'android' ? 0 : StyleSheet.hairlineWidth,
		borderTopWidth: Platform.OS === 'android' ? 0 : StyleSheet.hairlineWidth,
		borderColor: Colors.overlay15,
		flex: 1,
	},
});
